import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { api, ApiError, setCsrfToken } from '../lib/api';

interface AuthConfigResponse {
  providers: { google: boolean; shaw: boolean };
}

const AUTH_ERROR_MESSAGES: Record<string, string> = {
  google_domain_not_allowed: 'That Google account is outside the approved Workspace domain.',
  google_hd_mismatch: 'That Google account is outside the approved Workspace domain.',
  google_email_not_allowlisted: 'That Google account is not on the admin allowlist.',
  user_not_found: 'No account exists for that email — ask an admin to invite you first.',
  oidc_state_missing: 'Sign-in session expired. Please try again.',
  oidc_state_mismatch: 'Sign-in session was interrupted. Please try again.',
  oidc_email_missing: 'The identity provider did not return an email address.',
  oidc_code_missing: 'The identity provider did not return an authorization code.',
  access_denied: 'Sign-in was cancelled.',
};

export function Login() {
  const { refresh } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [mfaPending, setMfaPending] = useState(false);
  const [mfaCode, setMfaCode] = useState('');
  const [providers, setProviders] = useState<{ google: boolean; shaw: boolean }>({ google: false, shaw: false });

  // Fetch auth config so we know which OIDC buttons to show.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api<AuthConfigResponse>('/api/auth/config');
        if (!cancelled) setProviders(r.providers);
      } catch {
        // Quiet failure — falls back to local sign-in only.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Surface auth-redirect errors from the URL (e.g. `/?auth_error=user_not_found`).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('auth_error');
    if (code) {
      setErr(AUTH_ERROR_MESSAGES[code] ?? `Sign-in failed (${code}).`);
      params.delete('auth_error');
      const qs = params.toString();
      window.history.replaceState({}, '', `${window.location.pathname}${qs ? `?${qs}` : ''}`);
    }
  }, []);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      const r = await api<{ user?: unknown; csrfToken?: string; mfa_required?: boolean; userId?: string }>(
        '/api/auth/login',
        { method: 'POST', body: JSON.stringify({ email, password }) },
      );
      if (r.mfa_required) {
        setMfaPending(true);
        return;
      }
      if (r.csrfToken) setCsrfToken(r.csrfToken);
      await refresh();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Unable to sign in');
    } finally { setBusy(false); }
  }

  async function onSubmitMfa(e: React.FormEvent) {
    e.preventDefault();
    if (!mfaPending) return;
    setBusy(true); setErr(null);
    try {
      await api('/api/mfa/challenge', { method: 'POST', body: JSON.stringify({ code: mfaCode }) });
      await refresh();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Invalid code');
    } finally { setBusy(false); }
  }

  if (mfaPending) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <div className="text-center mb-10">
            <p className="qa-eyebrow mb-3">Two-factor authentication</p>
            <h1 className="qa-h1">One last <span className="qa-italic-accent">code.</span></h1>
            <p className="qa-deck text-base mt-3 text-ink-mute">
              Enter the 6-digit code from your authenticator (or a 10-character backup code).
            </p>
          </div>
          <form onSubmit={onSubmitMfa} className="qa-card p-8 space-y-5">
            <div>
              <label htmlFor="mfa-code" className="qa-label">Code</label>
              <input id="mfa-code" className="qa-input qa-mono text-center text-lg tracking-widest" autoFocus
                value={mfaCode} onChange={e => setMfaCode(e.target.value.replace(/\s/g, '').toLowerCase())}
                maxLength={10} />
            </div>
            {err && <div className="qa-badge qa-badge--out w-full justify-center">{err}</div>}
            <button type="submit" disabled={busy || mfaCode.length < 6} className="btn-primary w-full">
              {busy ? 'Verifying…' : 'Verify'}
            </button>
            <button type="button" onClick={() => { setMfaPending(false); setMfaCode(''); }} className="block text-center text-xs text-ink-mute hover:text-rust w-full">
              ← Back to sign in
            </button>
          </form>
        </div>
      </div>
    );
  }

  const anyOidc = providers.google || providers.shaw;

  return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <div className="text-center mb-10">
          <p className="qa-eyebrow mb-3">Client—Base</p>
          <h1 className="qa-display text-5xl">
            Advisory work, <span className="qa-italic-accent">organised.</span>
          </h1>
          <p className="qa-deck text-lg mt-3 text-ink-mute">Sign in to the advisory client platform.</p>
        </div>

        <div className="qa-card p-8 space-y-5">
          {/* OIDC buttons — only rendered if the server reports the provider as enabled. */}
          {providers.shaw && (
            <a href="/api/auth/shaw/start" className="btn-primary w-full block text-center">
              Sign in with Shaw and Partners
            </a>
          )}
          {providers.google && (
            <a href="/api/auth/google/start" className="btn-ghost w-full block text-center">
              Sign in with Google
            </a>
          )}

          {anyOidc && (
            <div className="flex items-center gap-3 pt-2">
              <div className="flex-1 h-px bg-[var(--qa-hairline-paper)]" />
              <span className="qa-eyebrow text-xs">Or with email</span>
              <div className="flex-1 h-px bg-[var(--qa-hairline-paper)]" />
            </div>
          )}

          <form onSubmit={onSubmit} className="space-y-5">
            <div>
              <label htmlFor="login-email" className="qa-label">Email</label>
              <input id="login-email" type="email" required value={email} onChange={e => setEmail(e.target.value)} className="qa-input" autoComplete="email" />
            </div>
            <div>
              <label htmlFor="login-password" className="qa-label">Password</label>
              <input id="login-password" type="password" required value={password} onChange={e => setPassword(e.target.value)} className="qa-input" autoComplete="current-password" />
            </div>
            {err && <div className="qa-badge qa-badge--out w-full justify-center">{err}</div>}
            <button type="submit" disabled={busy} className="btn-primary w-full">{busy ? 'Signing in…' : 'Sign in'}</button>
            <div className="flex justify-between text-xs pt-2">
              <a href="/forgot-password" className="text-ink-mute hover:text-rust">Forgot password?</a>
              <a href="/request-access" className="text-ink-mute hover:text-rust">Request access</a>
            </div>
            <p className="text-xs text-ink-mute text-center pt-1">
              Synthetic dev data only. MFA required in production.
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}
